import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PasswordResetForm } from '../components/PasswordResetForm';
import { useAuth } from '../hooks/useAuth';
import { useReturnUrl } from '../hooks/useReturnUrl';
import { useClientId } from '../hooks/useClientId';
import { useClientConfiguration } from '../hooks/useClientConfiguration';
import { ErrorBoundary } from '../components/ErrorBoundary';
import './AuthenticationPage.css';
import { ClientAwareLoginForm } from '../components/ClientAwareLoginForm';
import { GuestEmailForm } from '../components/GuestEmailForm';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useAllowGuest } from '../hooks/useAllowGuest';
import { ReactComponent as TrackerLogo } from '../assets/tracker-logo.svg';
import { ReactComponent as WeHuntLogo } from '../assets/wehunt-logo.svg';
import { ReactComponent as BurrelLogo } from '../assets/burrel-logo.svg';
import backgroundTopo from '../assets/background-topo.jpg';

type AuthMode = 'login' | 'reset-password';

/**
 * Main Authentication Page
 * Handles login, password reset, and redirect flow with return URLs
 */
export const AuthenticationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authState, validateReturnUrl } = useAuth();
  const { returnUrl, isValidReturnUrl, returnUrlError } = useReturnUrl();
  const { clientId } = useClientId();
  const { configuration, isLoading: configLoading } = useClientConfiguration(clientId);
  const { allowGuest } = useAllowGuest();

  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isCheckingAutoAuth, setIsCheckingAutoAuth] = useState(true);
  // Set when a signed-in user never receives a platform token, so the page can fall back to the
  // login form with an explanation instead of spinning forever
  const [autoAuthTimedOut, setAutoAuthTimedOut] = useState(false);
  const redirectAttemptedRef = React.useRef(false);

  // How long to wait for the token exchange before giving the user a way out. Generous enough to
  // cover a slow exchange on mobile data, short enough not to read as a hang.
  const AUTO_AUTH_TIMEOUT_MS = 10000;

  // Check if email login is enabled for the client
  const isEmailEnabled = configuration?.availableProviders?.includes('email') ?? false;

  // Determine auth mode from URL path on initial mount
  useEffect(() => {
    const path = window.location?.pathname || '/';
    if (path.includes('reset-password')) {
      setAuthMode('reset-password');
    } else {
      setAuthMode('login');
    }
  }, []);

  // Validate reset-password access after configuration loads
  useEffect(() => {
    // Only check if configuration has finished loading and we're in reset-password mode
    if (!configLoading && authMode === 'reset-password') {
      // If configuration is missing or email is not enabled, redirect back to login
      if (!configuration || !isEmailEnabled) {
        setAuthMode('login');
        const currentParams = new URLSearchParams(window.location.search);
        const newUrl = currentParams.toString() ? `/login?${currentParams.toString()}` : '/login';
        navigate(newUrl, { replace: true });
      }
    }
  }, [configLoading, configuration, isEmailEnabled, authMode, navigate]);

  // Validate return URL when it changes
  useEffect(() => {
    if (returnUrl && !isValidReturnUrl && !returnUrlError) {
      validateReturnUrl(returnUrl);
    }
  }, [returnUrl, isValidReturnUrl, returnUrlError, validateReturnUrl]);

  /**
   * Handle successful authentication
   */
  const handleAuthenticationSuccess = React.useCallback(async (platformToken: string, redirectUrl?: string) => {
    // Prevent multiple redirect attempts
    if (redirectAttemptedRef.current) {
      console.warn('Redirect already attempted, ignoring duplicate call');
      return;
    }

    console.log('handleAuthenticationSuccess called', {
      hasToken: !!platformToken,
      hasRedirectUrl: !!redirectUrl,
      redirectUrl,
      returnUrl,
      isValidReturnUrl
    });

    redirectAttemptedRef.current = true;
    setIsRedirecting(true);

    try {
      // Priority 1: If we have a redirect URL from the backend, use that (highest priority)
      // The backend redirectUrl already contains the token, so use it as-is
      if (redirectUrl && redirectUrl.trim().length > 0) {
        console.log('Redirecting to backend-provided URL', { redirectUrl });
        // Use window.location.href for external redirects - this is synchronous and will navigate
        window.location.href = redirectUrl;
        // Exit immediately - browser navigation should occur
        return;
      }

      // Priority 2: If we have a valid return URL, redirect there with the token
      if (returnUrl && isValidReturnUrl) {
        try {
          const url = new URL(returnUrl);
          url.searchParams.set('token', platformToken);
          url.searchParams.set('expires_at', authState.platformResponse?.expiresAt || '');
          
          console.log('Redirecting to return URL with token', { 
            returnUrl: url.toString(),
            hasToken: !!platformToken,
            expiresAt: authState.platformResponse?.expiresAt
          });
          
          // Redirect to external application
          window.location.href = url.toString();
          return;
        } catch (urlError) {
          console.error('Error constructing return URL:', urlError);
          // Fall through to default redirect
        }
      }

      // Default redirect to dashboard or home
      console.log('Redirecting to default dashboard');
      navigate('/dashboard');
    } catch (error) {
      console.error('Redirect error:', error);
      redirectAttemptedRef.current = false; // Reset on error so retry is possible
      setIsRedirecting(false);
      // Show error message or fallback behavior
    }
  }, [returnUrl, isValidReturnUrl, authState.platformResponse, navigate]);

  // Handle automatic authentication for returning users
  useEffect(() => {
    // Wait for auth state to be ready (not loading)
    if (authState.loading) {
      return undefined;
    }

    // Priority: If user is authenticated and we have a platform response, redirect immediately
    // This handles the case where useAuth automatically exchanges the token
    if (authState.isAuthenticated && authState.platformResponse) {
      console.log('User already authenticated with platform response, handling redirect');
      handleAuthenticationSuccess(
        authState.platformResponse.platformToken,
        authState.platformResponse.redirectUrl
      );
      return undefined;
    }

    // If user is authenticated but doesn't have platform response yet, wait for it.
    // The automatic token exchange in useAuth will set platformResponse.
    //
    // Bounded, because this branch used to wait forever: isCheckingAutoAuth stays true, so the page
    // renders nothing but a spinner. An Apple user whose redirect result was lost landed here and
    // sat on the loading screen with no error and no way back to the login form. If the exchange
    // has not produced a token by the timeout, surface the login form again so they can retry.
    if (authState.isAuthenticated && !authState.platformResponse) {
      console.log('User authenticated but no platform response yet, waiting for automatic exchange');

      const timeoutId = window.setTimeout(() => {
        console.error('Timed out waiting for the platform token exchange, returning to the login form');
        setAutoAuthTimedOut(true);
        setIsCheckingAutoAuth(false);
      }, AUTO_AUTH_TIMEOUT_MS);

      return () => window.clearTimeout(timeoutId);
    }

    // User is not authenticated, stop checking
    if (!authState.isAuthenticated) {
      console.log('User not authenticated, stopping auto-auth check');
      setIsCheckingAutoAuth(false);
    }

    return undefined;
  }, [authState.isAuthenticated, authState.platformResponse, authState.loading, handleAuthenticationSuccess]);

  /**
   * Handle mode switching
   */
  const handleModeSwitch = (mode: AuthMode) => {
    setAuthMode(mode);
    
    // Update URL without losing return URL parameter
    const newPath = mode === 'reset-password' ? '/reset-password' : '/login';
    const currentParams = new URLSearchParams(window.location.search);
    const newUrl = currentParams.toString() ? `${newPath}?${currentParams.toString()}` : newPath;
    
    navigate(newUrl, { replace: true });
  };

  /**
   * Handle password reset success
   */
  const handlePasswordResetSuccess = () => {
    // Switch back to login mode after successful password reset
    handleModeSwitch('login');
  };

  // Show loading state during redirect, auto-auth check, or initial auth state loading
  if (isRedirecting || isCheckingAutoAuth || authState.loading) {
    return (
      <div className="authentication-page">
        <div className="authentication-page__background">
          <div className="authentication-page__bg-bottom" style={{ backgroundImage: `url(${backgroundTopo})` }} />
          <div className="authentication-page__bg-top" style={{ backgroundImage: `url(${backgroundTopo})` }} />
        </div>
        <div className="authentication-page__loading">
          <div className="authentication-page__spinner" />
          <p>
            {isRedirecting
              ? t('authPage.loading.redirecting')
              : authState.loading
                ? t('authPage.loading.initializing')
                : t('authPage.loading.checkingAuth')}
          </p>
        </div>
      </div>
    );
  }

  // Show error if return URL is missing or invalid
  if (!isValidReturnUrl) {
    return (
      <ErrorBoundary>
        <div className="authentication-page">
          <div className="authentication-page__background">
            <div className="authentication-page__bg-bottom" style={{ backgroundImage: `url(${backgroundTopo})` }} />
            <div className="authentication-page__bg-top" style={{ backgroundImage: `url(${backgroundTopo})` }} />
          </div>
          <div className="authentication-page__container">
            <div className="authentication-page__logos">
              <div className="authentication-page__family-logos">
                <WeHuntLogo className="authentication-page__wehunt-logo" />
                <BurrelLogo className="authentication-page__burrel-logo" />
              </div>
              <TrackerLogo className="authentication-page__tracker-logo" />
            </div>

            <div className="authentication-page__content">
              <div className="authentication-page__error">
                <h2>{t('authPage.returnUrlError.heading')}</h2>
                <p className="authentication-page__error-message">
                  {returnUrlError ? t(returnUrlError) : null}
                </p>
                <p className="authentication-page__error-note">
                  {t('authPage.returnUrlError.note')}
                </p>
                <div className="authentication-page__error-details">
                  <h3>{t('authPage.returnUrlError.requiredParamsHeading')}</h3>
                  <ul>
                    <li><code>client_id</code> - {t('authPage.returnUrlError.clientIdParam')}</li>
                    <li><code>return_url</code> - {t('authPage.returnUrlError.returnUrlParam')}</li>
                  </ul>
                  <p className="authentication-page__error-example">
                    {t('authPage.returnUrlError.exampleLabel')} <code>?client_id=myapp&return_url=https://myapp.com/auth/callback</code>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="authentication-page">
        <div className="authentication-page__background">
          <div className="authentication-page__bg-bottom" style={{ backgroundImage: `url(${backgroundTopo})` }} />
          <div className="authentication-page__bg-top" style={{ backgroundImage: `url(${backgroundTopo})` }} />
        </div>

        <div className={`authentication-page__container ${allowGuest ? 'authentication-page__container--guest-mode' : ''}`}>
          {/* Logos header */}
          <div className="authentication-page__logos">
            <div className="authentication-page__family-logos">
              <WeHuntLogo className="authentication-page__wehunt-logo" />
              <BurrelLogo className="authentication-page__burrel-logo" />
            </div>
            <TrackerLogo className="authentication-page__tracker-logo" />
          </div>

          {/* Header text */}
          <div className="authentication-page__header">
            {allowGuest ? (
              <h1 className="authentication-page__title">
                {t('authPage.header.guestTitle')}
              </h1>
            ) : (
              <>
                <h1 className="authentication-page__title">
                  {t('authPage.header.title')}
                </h1>
                <p className="authentication-page__subtitle">
                  {t('authPage.header.subtitle')}
                </p>
              </>
            )}
          </div>

          {/* Login form */}
          <div className={`authentication-page__content ${allowGuest ? 'authentication-page__content--two-column' : ''}`}>
            <div className="authentication-page__login-column">
              {autoAuthTimedOut && authMode === 'login' && (
                <div className="authentication-page__auth-timeout" role="alert" aria-live="polite">
                  {t('authPage.autoAuthTimeout')}
                </div>
              )}
              {authMode === 'login' ? (
                <ClientAwareLoginForm
                  returnUrl={returnUrl || undefined}
                  onSuccess={handleAuthenticationSuccess}
                />
              ) : (
                <PasswordResetForm
                  returnUrl={returnUrl || undefined}
                  onSuccess={handlePasswordResetSuccess}
                />
              )}
            </div>

            {allowGuest && (
              <>
                <div className="authentication-page__column-separator" />
                <div className="authentication-page__guest-column">
                  <GuestEmailForm returnUrl={returnUrl!} />
                </div>
              </>
            )}
          </div>

          {/* Password reset link - only show for email-enabled clients */}
          {authMode === 'login' && isEmailEnabled && (
            <div className="authentication-page__mode-switch">
              <p>
                {t('authPage.modeSwitch.forgotPassword')}{' '}
                <button
                  type="button"
                  onClick={() => handleModeSwitch('reset-password')}
                  className="authentication-page__link-button"
                >
                  {t('authPage.modeSwitch.resetHere')}
                </button>
              </p>
            </div>
          )}

          {authMode === 'reset-password' && (
            <div className="authentication-page__mode-switch">
              <p>
                {t('authPage.modeSwitch.rememberPassword')}{' '}
                <button
                  type="button"
                  onClick={() => handleModeSwitch('login')}
                  className="authentication-page__link-button"
                >
                  {t('authPage.modeSwitch.signInHere')}
                </button>
              </p>
            </div>
          )}
        </div>

        {/* Page footer */}
        <footer className="authentication-page__footer">
          <nav className="authentication-page__legal-links">
            <a href="https://natlink.se/language/en/legal/" target="_blank" rel="noopener noreferrer">{t('authPage.footer.gdpr')}</a>
            <span className="authentication-page__legal-separator">|</span>
            <a href="https://natlink.se/language/en/legal/" target="_blank" rel="noopener noreferrer">{t('authPage.footer.cookieSettings')}</a>
            <span className="authentication-page__legal-separator">|</span>
            <a href="https://natlink.se/language/en/legal/" target="_blank" rel="noopener noreferrer">{t('authPage.footer.terms')}</a>
            <span className="authentication-page__legal-separator">|</span>
            <a href="https://natlink.se/language/en/legal/" target="_blank" rel="noopener noreferrer">{t('authPage.footer.privacy')}</a>
            <span className="authentication-page__legal-separator">|</span>
            <a href="https://natlink.se/" target="_blank" rel="noopener noreferrer">{t('authPage.footer.natlinkWebsite')}</a>
          </nav>
          <div className="authentication-page__footer-info">
            <p className="authentication-page__address">{t('authPage.footer.address')}</p>
            <p className="authentication-page__copyright">{t('authPage.footer.copyright')}</p>
          </div>
          <div className="authentication-page__footer-language">
            <LanguageSwitcher />
          </div>
        </footer>
      </div>
    </ErrorBoundary>
  );
};